import { FC, useContext, useEffect, useMemo } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { CountryNames, selectableCountryRegionData } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { SectionWrapper } from "@/canvas/features/FormV2/blocks/payment/SectionWrapper";
import {
  useIsBuyerBillingCollected,
  useShippingAddressSectionShouldRender,
} from "@/canvas/features/FormV2/steps/step2/hooks/useBuyerInformationRenderPosition";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { buildBuyerFullName, copyBillingToShippingFields } from "@/common/helpers/fulfillmentCheckout";
import { isCompanyNameValid, isRequired } from "@/common/helpers/validators";
import { COUNTRY_NAME_OPTIONS } from "@/common/constants/countryOptions";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { useGoogleMapsAutocomplete } from "@/common/hooks/useGoogleMapsAutocomplete";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormSearchableSelectField } from "@/forms-design-system/FormSearchableSelectField";
import { FormTextField } from "@/forms-design-system/FormTextField";

const SHIPPING_STREET_INPUT_ID = "shipping-street-address-input";

export const ShippingAddressSection: FC = () => {
  const { t } = useTranslate();
  const { organization, displayAddressQuestion } = useContext(FrontendFormContext);
  const { control, setValue, getValues, watch } = useFormContext<TicketingPaymentInput>();

  const isVisible = useShippingAddressSectionShouldRender();

  const isBillingCollected = useIsBuyerBillingCollected();
  const canOfferSameAsBilling = displayAddressQuestion && isBillingCollected;
  const shippingSameAsBilling = watch("shippingSameAsBilling") !== false;
  const useSameAsBilling = canOfferSameAsBilling && shippingSameAsBilling;
  const showShippingAddressFields = !useSameAsBilling;

  const canOfferSameFullNameAsBuyer = !displayAddressQuestion && isBillingCollected;
  const shippingSameFullNameAsBuyer = watch("shippingSameFullNameAsBuyer") !== false;
  const useSameFullNameAsBuyer = canOfferSameFullNameAsBuyer && shippingSameFullNameAsBuyer;
  const buyerFullName = buildBuyerFullName({
    firstName: watch("firstName"),
    lastName: watch("lastName"),
    companyName: watch("companyName"),
  });
  const showFullNameField = showShippingAddressFields && !useSameFullNameAsBuyer;

  const shippingCountry = watch("shippingCountry");
  const displayStateSelect = shippingCountry === CountryNames.Canada || shippingCountry === CountryNames.UnitedStates;
  const { regionOptions } = useCountryRegion(shippingCountry);

  const shippingCountryCode = selectableCountryRegionData.find(
    (country) => country.countryName === shippingCountry
  )?.countryShortCode;
  const componentRestrictions = useMemo(
    () => (shippingCountryCode ? { country: [shippingCountryCode.toLowerCase()] } : undefined),
    [shippingCountryCode]
  );

  const { addressString, addressComponents } = useGoogleMapsAutocomplete({
    addressInputId: SHIPPING_STREET_INPUT_ID,
    defaultAddressString: getValues("shippingAddress") ?? undefined,
    organizationCountry: organization?.country,
    componentRestrictions,
    disabled: !isVisible || useSameAsBilling,
  });

  useEffect(() => {
    if (useSameAsBilling) {
      return;
    }
    if (addressString !== undefined) {
      setValue("shippingAddress", addressString ?? "", { shouldDirty: true });
    }
    if (!addressComponents) {
      return;
    }
    setValue("shippingCity", addressComponents.city ?? "", { shouldDirty: true });
    setValue("shippingPostalCode", addressComponents.postalCode ?? "", { shouldDirty: true });
    setValue("shippingRegion", addressComponents.region ?? "", { shouldDirty: true });
    const selectedCountry = selectableCountryRegionData.find(
      (country) => country.countryShortCode === addressComponents.countryCode
    );
    if (selectedCountry) {
      setValue("shippingCountry", selectedCountry.countryName, { shouldDirty: true });
    }
  }, [addressComponents, addressString, useSameAsBilling, setValue]);

  if (!isVisible) {
    return null;
  }

  return (
    <SectionWrapper
      title={t("ticketing", "fulfillment.shippingAddress")}
      dataTest="fulfillment-shipping-address-section"
    >
      <Stack gap={2}>
        {canOfferSameAsBilling && (
          <FormCheckbox
            size="medium"
            checked={shippingSameAsBilling}
            onChange={(_, checked) => {
              setValue("shippingSameAsBilling", checked, { shouldDirty: true });
              if (!checked) {
                const fields = copyBillingToShippingFields(getValues());
                setValue("shippingFullName", fields.shippingFullName ?? "", { shouldDirty: true });
                setValue("shippingAddress", fields.shippingAddress ?? "", { shouldDirty: true });
                setValue("shippingCity", fields.shippingCity ?? "", { shouldDirty: true });
                setValue("shippingPostalCode", fields.shippingPostalCode ?? "", { shouldDirty: true });
                setValue("shippingRegion", fields.shippingRegion ?? "", { shouldDirty: true });
                setValue("shippingCountry", fields.shippingCountry ?? "", { shouldDirty: true });
              }
            }}
            label={t("ticketing", "fulfillment.sameAsBilling")}
            data-test="fulfillment-same-as-billing"
          />
        )}
        {canOfferSameFullNameAsBuyer && (
          <FormCheckbox
            size="medium"
            checked={shippingSameFullNameAsBuyer}
            onChange={(_, checked) => {
              setValue("shippingSameFullNameAsBuyer", checked, { shouldDirty: true });
              if (!checked) {
                setValue("shippingFullName", buyerFullName, { shouldDirty: true });
              }
            }}
            label={t("ticketing", "fulfillment.sameFullNameAsBuyer")}
            data-test="fulfillment-same-full-name-as-buyer"
          />
        )}
        {showShippingAddressFields && (
          <Stack gap={2}>
            {showFullNameField && (
              <FormTextField
                control={control}
                name="shippingFullName"
                rules={[
                  isRequired(true, t("common", "isRequired")),
                  isCompanyNameValid(t("common", "invalidCharacterUsed")),
                ]}
                label={t("ticketing", "fulfillment.fullName")}
                placeholder={t("ticketing", "fulfillment.fullNamePlaceholder")}
                data-test="fulfillment-shipping-full-name"
                required
              />
            )}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  name="shippingAddress"
                  id={SHIPPING_STREET_INPUT_ID}
                  label={t("ticketing", "fulfillment.streetAddress")}
                  placeholder={t("ticketing", "fulfillment.streetAddressPlaceholder")}
                  rules={[isRequired(true, t("common", "isRequired"))]}
                  required
                  data-test="fulfillment-shipping-street"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  control={control}
                  name="shippingCity"
                  rules={[isRequired(true, t("common", "isRequired"))]}
                  label={t("ticketing", "fulfillment.city")}
                  data-test="fulfillment-shipping-city"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  control={control}
                  name="shippingPostalCode"
                  rules={[isRequired(true, t("common", "isRequired"))]}
                  label={t("ticketing", "fulfillment.postalCode")}
                  data-test="fulfillment-shipping-postal-code"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={displayStateSelect ? 6 : 12}>
                <FormSearchableSelectField
                  control={control}
                  name="shippingCountry"
                  options={COUNTRY_NAME_OPTIONS}
                  rules={[isRequired(true, t("common", "isRequired"))]}
                  onChange={() => setValue("shippingRegion", "", { shouldDirty: true })}
                  label={t("ticketing", "country")}
                  placeholder={t("ticketing", "fulfillment.countryPlaceholder")}
                  data-test="fulfillment-shipping-country"
                  required
                />
              </Grid>
              {displayStateSelect && (
                <Grid item xs={12} sm={6}>
                  <FormSearchableSelectField
                    control={control}
                    name="shippingRegion"
                    options={regionOptions}
                    rules={[isRequired(displayStateSelect, t("common", "isRequired"))]}
                    label={shippingCountry === CountryNames.Canada ? t("ticketing", "region") : t("ticketing", "state")}
                    data-test="fulfillment-shipping-region"
                    required
                  />
                </Grid>
              )}
            </Grid>
          </Stack>
        )}
      </Stack>
    </SectionWrapper>
  );
};
